import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Eye, Layers3, MessageCircle, Sparkles } from 'lucide-react';
import { PublicPageShell } from '../../components/layout/PublicPageShell';
import { PageSeo } from '../../components/seo/PageSeo';
import { useAppSelector } from '../../hooks/useAppRedux';
import { useI18n } from '../../hooks/useI18n';
import { buildLocalizedPath } from '../../seo/urls';

export const LandingInfoPage = ({ type }) => {
  const { t, language, formatNumber } = useI18n();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const title = type === 'docs' ? t('docsTitle') : t('aboutTitle');
  const body = type === 'docs' ? t('docsBody') : t('aboutBody');
  const highlightIcons = [Sparkles, MessageCircle, Eye];
  const aboutStats = t('aboutStats') || [];
  const aboutHighlights = t('aboutHighlights') || [];
  const aboutFlow = t('aboutFlow') || [];
  const DirectionArrow = language === 'fa' ? ArrowLeft : ArrowRight;
  const activePath = type === 'docs' ? '/docs' : '/about';

  return (
    <PublicPageShell activePath={activePath}>
      {type === 'about' ? <PageSeo page="about" language={language} /> : null}

      {type === 'docs' ? (
        <section className="flex flex-1 items-center justify-center py-16 text-center">
          <div className="glass-panel max-w-2xl rounded-[2rem] border border-border bg-panel p-8 shadow-floating backdrop-blur-2xl sm:p-10">
            <span className="inline-flex rounded-full border border-border bg-panel-strong px-3 py-1.5 text-xs font-bold text-muted">
              {t('landingBrand')}
            </span>
            <h1 className="mt-6 text-3xl font-black tracking-[-0.04em] text-text sm:text-5xl">{title}</h1>
            <p className="mt-5 text-base leading-8 text-muted sm:text-lg">{body}</p>
            <Link
              to={buildLocalizedPath('/', language)}
              className="mt-8 inline-flex min-h-12 items-center justify-center rounded-2xl bg-accent px-6 text-sm font-bold text-white shadow-sm transition hover:opacity-95 dark:text-slate-950"
            >
              {t('navHome')}
            </Link>
          </div>
        </section>
      ) : (
        <section className="flex-1 py-10 sm:py-14">
          <div className="grid items-center gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:gap-12">
            <div className="text-start">
              <span className="inline-flex rounded-full border border-accent/20 bg-accent-soft px-4 py-2 text-xs font-bold text-accent">
                {t('aboutEyebrow')}
              </span>
              <h1 className="mt-6 max-w-3xl text-4xl font-black leading-tight tracking-[-0.055em] text-text sm:text-5xl lg:text-6xl">
                {title}
              </h1>
              <p className="mt-5 max-w-2xl text-base leading-8 text-muted sm:text-lg">{t('aboutLead')}</p>
              <p className="mt-4 max-w-2xl text-sm leading-7 text-muted">{body}</p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  to={buildLocalizedPath('/', language)}
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-accent px-6 text-sm font-bold text-white shadow-sm transition hover:opacity-95 dark:text-slate-950"
                >
                  {t('aboutPrimaryCta')}
                  <DirectionArrow size={17} strokeWidth={2.25} />
                </Link>
                <Link
                  to={isAuthenticated ? '/projects' : '/login'}
                  className="inline-flex min-h-12 items-center justify-center rounded-2xl border border-border bg-panel/80 px-6 text-sm font-bold text-text shadow-sm transition hover:border-accent/30 hover:bg-panel"
                >
                  {t('aboutSecondaryCta')}
                </Link>
              </div>

              <div className="mt-8 grid gap-3 sm:grid-cols-3">
                {aboutStats.map((stat) => (
                  <div key={stat.label} className="rounded-3xl border border-border bg-panel/70 p-4 shadow-sm backdrop-blur-xl">
                    <p className="text-2xl font-black text-text">{stat.value}</p>
                    <p className="mt-1 text-xs font-medium leading-5 text-muted">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="glass-panel relative overflow-hidden rounded-[2.2rem] border border-border bg-panel p-5 shadow-floating backdrop-blur-2xl sm:p-6">
              <div className="absolute -right-20 -top-20 h-48 w-48 rounded-full bg-accent/15 blur-3xl" />
              <div className="absolute -bottom-24 left-8 h-56 w-56 rounded-full bg-teal-400/15 blur-3xl" />
              <div className="relative rounded-[1.7rem] border border-border bg-panel-strong p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="grid h-11 w-11 place-items-center rounded-2xl bg-accent text-white dark:text-slate-950">
                      <Layers3 size={21} strokeWidth={2.2} />
                    </div>
                    <div>
                      <p className="text-sm font-black">{t('aboutPreviewTitle')}</p>
                      <p className="text-xs text-muted">{t('aboutPreviewSubtitle')}</p>
                    </div>
                  </div>
                  <span className="rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-bold text-emerald-600 dark:text-emerald-300">
                    {t('landingGeneratedBadge')}
                  </span>
                </div>

                <div className="mt-6 space-y-3">
                  {aboutFlow.map((step, index) => (
                    <div key={step.title} className="rounded-2xl border border-border/80 bg-app/70 p-4">
                      <div className="flex items-start gap-3">
                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-accent-soft text-xs font-black text-accent">
                          {formatNumber(index + 1)}
                        </span>
                        <div>
                          <p className="text-sm font-bold text-text">{step.title}</p>
                          <p className="mt-1 text-xs leading-5 text-muted">{step.body}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 grid gap-4 md:grid-cols-3">
            {aboutHighlights.map((item, index) => {
              const Icon = highlightIcons[index] || Sparkles;

              return (
                <article key={item.title} className="rounded-[1.75rem] border border-border bg-panel/75 p-5 shadow-sm backdrop-blur-xl">
                  <div className="grid h-11 w-11 place-items-center rounded-2xl bg-accent-soft text-accent">
                    <Icon size={21} strokeWidth={2.2} />
                  </div>
                  <h2 className="mt-5 text-lg font-black text-text">{item.title}</h2>
                  <p className="mt-3 text-sm leading-7 text-muted">{item.body}</p>
                </article>
              );
            })}
          </div>
        </section>
      )}
    </PublicPageShell>
  );
};
